import { Button, HStack, Stack } from "@chakra-ui/react";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnswerSeV2QuestionMutation, useGetSourcingEventQuery } from "../../../../autogen/bff-api";
import { AttachmentPicker } from "../../../../common/components/communication/AttachmentPicker";
import { richTextHasContent } from "../../../../common/components/communication/communicationUtils";
import { useMessageAttachments } from "../../../../common/components/communication/useMessageAttachments";
import { useApiError } from "../../../../common/errors/useApiError";
import { TipTap } from "../../../../common/input/TipTap/TipTap";

export const AnswerForm = ({
  eventId,
  questionId,
  trailingAction,
  onAnswerChange,
}: {
  eventId: string;
  questionId: string;
  trailingAction?: ReactNode;
  onAnswerChange?: (answer: string) => void;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const [answer, setAnswer] = useState("");

  useEffect(() => {
    onAnswerChange?.(answer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer]);
  const [resetKey, setResetKey] = useState(0);
  const [answerQuestion, { isLoading }] = useAnswerSeV2QuestionMutation();
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const requestDocuments = (event?.eventData.documents ?? []).map((d) => ({ id: d.id, name: d.name }));
  const attachments = useMessageAttachments({
    eventId,
    uploadEntityType: "SeV2AnswerAttachment",
    pollRole: "buyer",
    questionId,
  });

  const handleSubmit = async () => {
    if (!richTextHasContent(answer)) return;
    try {
      await answerQuestion({
        eventId,
        questionId,
        seV2QuestionPatchDto: { answer, attachments: attachments.toInputs() },
      }).unwrap();
      setAnswer("");
      attachments.reset();
      setResetKey((key) => key + 1);
    } catch (error) {
      displayer.trigger(error);
    }
  };

  return (
    <Stack spacing="3" bg="smBackground" borderWidth="1px" borderColor="smBorder" rounded="md" p="3">
      <TipTap key={resetKey} content="" editable debounceWait={0} onUpdate={(value) => setAnswer(value)} />
      <AttachmentPicker
        controls={attachments}
        requestDocuments={requestDocuments}
      />
      <HStack justify="flex-end" spacing="3">
        <Button
          size="sm"
          colorScheme="teal"
          onClick={handleSubmit}
          isLoading={isLoading}
          isDisabled={
            !richTextHasContent(answer) || attachments.isUploading || attachments.hasFailedUploads
          }
        >
          {t("Send private answer")}
        </Button>
        {trailingAction}
      </HStack>
    </Stack>
  );
};
